// The honest odds of a play. A price with no drift, moving like Brownian motion at the
// volatility Hyperliquid's own 1m candles show, against two lines: the bust below and the
// target above. These are estimates for the screen, never money: every stake, payout and
// bust price still comes from the SDK in exact integers.

/** Standard normal CDF (Abramowitz and Stegun 7.1.26 through erf), good to about 1e-7. */
export function phi(x: number): number {
  const t = 1 / (1 + 0.3275911 * (Math.abs(x) / Math.SQRT2));
  const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  const erf = 1 - poly * Math.exp(-(x * x) / 2);
  return x >= 0 ? 0.5 * (1 + erf) : 0.5 * (1 - erf);
}

/** Per-minute volatility of log returns from closes a minute apart (oldest first). */
export function sigmaPerMinute(closes: number[]): number | null {
  const returns: number[] = [];
  for (let i = 1; i < closes.length; i += 1) {
    const a = closes[i - 1];
    const b = closes[i];
    if (a && b && a > 0 && b > 0) returns.push(Math.log(b / a));
  }
  if (returns.length < 20) return null;
  const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  const variance = returns.reduce((sum, r) => sum + (r - mean) ** 2, 0) / (returns.length - 1);
  const sigma = Math.sqrt(variance);
  return Number.isFinite(sigma) && sigma > 0 ? sigma : null;
}

/** Chance the price touches a line `distance` away (a fraction: 0.001 is 0.1%) within `minutes`. */
export function touchWithin(distance: number, sigma: number, minutes: number): number {
  if (distance <= 0) return 1;
  if (minutes <= 0) return 0;
  return Math.min(1, 2 * (1 - phi(distance / (sigma * Math.sqrt(minutes)))));
}

/** Chance the target (`up` away) comes before the bust (`down` away): the gambler's ruin. */
export function targetFirst(up: number, down: number): number {
  if (up <= 0) return 1;
  if (down <= 0) return 0;
  return down / (up + down);
}

/** Expected minutes until one of the two lines is touched. */
export function expectedMinutes(up: number, down: number, sigma: number): number {
  return (up * down) / (sigma * sigma);
}

/** Minutes until a lone line `distance` away has been touched with chance `p`. */
export function minutesToChance(distance: number, sigma: number, p = 0.5): number {
  // 2(1 − Φ(z)) = p  →  z = Φ⁻¹(1 − p/2)
  const z = inversePhi(1 - p / 2);
  return (distance / (sigma * z)) ** 2;
}

/** Inverse standard normal CDF (Acklam). */
export function inversePhi(p: number): number {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  const low = 0.02425;
  const at = (xs: number[], i: number) => xs[i] ?? 0;
  if (p < low) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((at(c, 0) * q + at(c, 1)) * q + at(c, 2)) * q + at(c, 3)) * q + at(c, 4)) * q + at(c, 5)) /
      ((((at(d, 0) * q + at(d, 1)) * q + at(d, 2)) * q + at(d, 3)) * q + 1)
    );
  }
  if (p > 1 - low) return -inversePhi(1 - p);
  const q = p - 0.5;
  const r = q * q;
  return (
    ((((((at(a, 0) * r + at(a, 1)) * r + at(a, 2)) * r + at(a, 3)) * r + at(a, 4)) * r + at(a, 5)) * q) /
    (((((at(b, 0) * r + at(b, 1)) * r + at(b, 2)) * r + at(b, 3)) * r + at(b, 4)) * r + 1)
  );
}

/** `38s`, `4m`, `2h 10m`, `3d`: a duration for the screen. */
export function span(minutes: number): string {
  if (!Number.isFinite(minutes)) return "—";
  const seconds = Math.max(1, Math.round(minutes * 60));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(minutes);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return m % 60 && h < 10 ? `${h}h ${m % 60}m` : `${h}h`;
  return `${Math.round(h / 24)}d`;
}

/** `39s`, `3m`, `1.7h`, `26h`, `3d`: a duration in at most four characters. */
export function short(minutes: number): string {
  if (!Number.isFinite(minutes)) return "—";
  if (minutes < 1) return `${Math.max(1, Math.round(minutes * 60))}s`;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 10) return `${hours.toFixed(1)}h`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/** A chance as a whole percentage for the screen: `<1%` and `>99%` at the ends. */
export function pct(p: number): string {
  if (!Number.isFinite(p)) return "—";
  if (p > 0 && p < 0.01) return "<1%";
  if (p < 1 && p > 0.99) return ">99%";
  return `${Math.round(p * 100)}%`;
}
